import { usePageMeta } from '../lib/usePageMeta';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AppStoreButton } from '../components/shared/AppStoreButton';
import { PhoneFrame } from '../components/shared/PhoneFrame';
import { TireShops } from '../components/shared/TireShops';


/* ------------------------------------------------------------------------ */
/* Motion helpers                                                           */
/* ------------------------------------------------------------------------ */

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** True once the element has scrolled into view. */
const useInView = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === 'undefined') return setSeen(true);
    const io = new IntersectionObserver(([e]) => e.isIntersecting && (setSeen(true), io.disconnect()), { rootMargin: '0px 0px -12% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen] as const;
};

/** Fades and lifts its content in the first time it scrolls into view. */
const Reveal = ({ children, className = '', delay = 0 }: { children: ReactNode; className?: string; delay?: number }) => {
  const [ref, seen] = useInView<HTMLDivElement>();
  return (
    <div ref={ref} className={`reveal ${seen ? 'is-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const Stars = ({ n = 5, className = '' }: { n?: number; className?: string }) => (
  <span className={`inline-flex gap-0.5 ${className}`} aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} className={`w-4 h-4 ${i < Math.round(n) ? 'text-[#F5A524]' : 'text-[#D8DAE0]'}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9z" />
      </svg>
    ))}
  </span>
);

// Real App Store reviews (US), quoted as written.
const REVIEWS = [
  { title: 'Fast, accurate and super useful', body: 'Finally, an easy way to check tire health without an appointment. The AI detection is surprisingly fast and the report is easy to understand.', date: 'Jan 2026' },
  { title: 'Pretty handy', body: 'This is a good replacement for the penny test. Considering pennies are gradually getting out of circulation, just get your phone close to the tire and take a picture.', date: 'Jan 2026' },
  { title: 'The best I’ve seen', body: 'So far this the best app for tracking your tires.', date: 'Mar 2026' },
  { title: 'Saved me a trip', body: 'Nice! Saved me a trip to the shop, simple and actually useful!', date: 'Jan 2026' },
  { title: 'Excellent', body: 'So easy to use and good info.', date: 'Jul 2026' },
  { title: 'Useful app', body: 'Useful app to check tires.', date: 'May 2026' },
];

const FEATURES = [
  {
    title: 'Tread depth',
    body: 'Measured from a photo of the tread, in 32nds or millimetres.',
    art: (
      <svg viewBox="0 0 160 70" className="w-full h-auto" aria-hidden="true">
        <rect x="0" y="52" width="160" height="18" rx="5" fill="#2C2D32" />
        {[0, 1, 2, 3, 4].map((i) => <rect key={i} x={6 + i * 31} y="22" width="25" height="32" rx="5" fill="#3D3E45" />)}
        <line x1="34" x2="34" y1="22" y2="52" stroke="#93ABF8" strokeWidth="2" />
        <text x="44" y="16" fill="#fff" fontSize="12" fontWeight="600" fontFamily="Roboto Flex, sans-serif">6.9 mm</text>
      </svg>
    ),
  },
  {
    title: 'Tire age',
    body: 'Read from the DOT code on the sidewall. Rubber ages even when tread looks fine.',
    art: (
      <svg viewBox="0 0 160 70" className="w-full h-auto" aria-hidden="true">
        <text x="6" y="42" fill="#8E9099" fontSize="13" fontWeight="600" letterSpacing="1.5" fontFamily="Roboto Flex, sans-serif">DOT 4B2X</text>
        <ellipse cx="122" cy="37" rx="26" ry="16" fill="none" stroke="#93ABF8" strokeWidth="2" />
        <text x="122" y="42" fill="#fff" fontSize="14" textAnchor="middle" fontWeight="700" fontFamily="Roboto Flex, sans-serif">3823</text>
        <text x="122" y="66" fill="#93ABF8" fontSize="10" textAnchor="middle" fontFamily="Roboto Flex, sans-serif">3.1 years</text>
      </svg>
    ),
  },
  {
    title: 'Wear pattern',
    body: 'Even, center or edge wear, which can point to pressure or alignment problems.',
    art: (
      <svg viewBox="0 0 160 70" className="w-full h-auto" aria-hidden="true">
        <path d="M18 56 C18 26 40 16 80 16 C120 16 142 26 142 56 Z" fill="#3D3E45" />
        <path d="M118 18 C132 22 142 32 142 56 L122 56 C122 38 120 26 112 19 Z" fill="#E8833A" />
        <text x="80" y="44" fill="#fff" fontSize="11" textAnchor="middle" fontFamily="Roboto Flex, sans-serif">outer edge</text>
      </svg>
    ),
  },
  {
    title: 'A report to share',
    body: 'A PDF per car, ready for your shop, a buyer or your family.',
    art: (
      <svg viewBox="0 0 160 70" className="w-full h-auto" aria-hidden="true">
        <rect x="52" y="10" width="46" height="56" rx="5" fill="#4A4C55" transform="rotate(-8 75 38)" />
        <rect x="62" y="6" width="46" height="58" rx="5" fill="#fff" />
        <rect x="68" y="14" width="22" height="4" rx="2" fill="#2B63E6" />
        {[24, 31, 38, 45].map((y) => <rect key={y} x="68" y={y} width={y === 45 ? 20 : 34} height="3" rx="1.5" fill="#D5D8E0" />)}
        <rect x="68" y="52" width="14" height="6" rx="3" fill="#2E7D32" />
      </svg>
    ),
  },
];

/** Horizontally scrolling reviews with arrows; swipe on touch. */
const ReviewSlider = () => {
  const ref = useRef<HTMLDivElement>(null);
  const by = (dir: number) => {
    const el = ref.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    el.scrollBy({ left: dir * ((card?.offsetWidth ?? 320) + 16), behavior: reducedMotion() ? 'auto' : 'smooth' });
  };
  return (
    <div>
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl md:text-[2.6rem] leading-[1.1] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 110' }}>What drivers say</h2>
          <p className="mt-3 text-muted text-lg flex items-center gap-2"><Stars n={4.3} /> 4.3 on the App Store</p>
        </div>
        <div className="hidden sm:flex gap-2">
          {[-1, 1].map((d) => (
            <button key={d} type="button" onClick={() => by(d)} aria-label={d < 0 ? 'Previous reviews' : 'Next reviews'} className="h-11 w-11 rounded-full border border-line inline-flex items-center justify-center hover:bg-surface">
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true"><path d={d < 0 ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          ))}
        </div>
      </div>
      <div ref={ref} className="no-scrollbar mt-8 flex gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 scroll-px-4 sm:mx-0 sm:px-0 sm:scroll-px-0 pb-2">
        {REVIEWS.map((r) => (
          <figure key={r.title} className="snap-start shrink-0 w-[82%] sm:w-[calc(50%-8px)] lg:w-[calc(33.333%-11px)] rounded-3xl bg-surface p-6 flex flex-col">
            <Stars />
            <blockquote className="mt-4 flex-1">
              <p className="font-semibold text-lg leading-snug">“{r.title}”</p>
              <p className="mt-2 text-muted leading-relaxed">{r.body}</p>
            </blockquote>
            <figcaption className="mt-5 text-sm text-muted">App Store review · {r.date}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Pieces                                                                   */
/* ------------------------------------------------------------------------ */

type Zone = { min: number; label: string; color: string; badge: string; advice: string };

// Same depth bands as the app's report (backend/models/config.py); 1.6 mm (2/32″) is the US legal minimum.
const ZONES: Zone[] = [
  { min: 4, label: 'Good', color: '#2E7D32', badge: 'bg-good-bg text-good-fg', advice: 'Plenty of grip left. Check again in a few months.' },
  { min: 2.5, label: 'Replace soon', color: '#E8833A', badge: 'bg-[#FFE2CC] text-[#8A3F06]', advice: 'Fine in the dry, weaker in rain and snow. Start pricing new tires.' },
  { min: 1.6, label: 'Replace', color: '#D93A2E', badge: 'bg-replace-bg text-replace-fg', advice: 'Wet-road braking is much longer now. Replace these tires.' },
  { min: 0, label: 'Below legal limit', color: '#8C1D18', badge: 'bg-replace-bg text-replace-fg', advice: 'Under 2/32″, the legal minimum in most US states. Replace before driving far.' },
];

const MIN = 0.5;
const MAX = 8;
const at = (mm: number) => ((mm - MIN) / (MAX - MIN)) * 100;
// Hard-stop gradient so the slider track itself shows the bands.
const TRACK = `linear-gradient(90deg, #8C1D18 0 ${at(1.6)}%, #D93A2E ${at(1.6)}% ${at(2.5)}%, #E8833A ${at(2.5)}% ${at(4)}%, #2E7D32 ${at(4)}% 100%)`;

/** Drag the depth and watch the tread blocks wear down to the wear bars. */
const TreadRuler = () => {
  const [mm, setMm] = useState(4.6);
  const zone = ZONES.find((z) => mm >= z.min)!;
  // Round 32nds down: 1.2 mm should read 1/32″, not 2/32″ (the legal minimum).
  const thirtySeconds = Math.floor((mm / 25.4) * 32 + 1e-6);

  // Cross-section geometry (SVG units): 10 blocks on a carcass, wear bars in the grooves.
  const W = 440, BASE = 118, FULL = 100;
  const h = (d: number) => (d / MAX) * FULL;
  const blocks = 8, groove = 14, bw = (W - groove * (blocks - 1)) / blocks;
  const atLimit = mm <= 1.6;

  // iPhone Safari only moves a range input when the thumb itself is dragged;
  // jump to wherever the track is touched, then let the native drag continue.
  const jump = (e: React.PointerEvent<HTMLInputElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const thumb = 30;
    const f = Math.min(1, Math.max(0, (e.clientX - r.left - thumb / 2) / (r.width - thumb)));
    setMm(Math.round((MIN + f * (MAX - MIN)) * 10) / 10);
  };

  return (
    <section className="bg-rubber text-white overflow-hidden" aria-labelledby="ruler-title">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-24 grid gap-10 md:gap-12 md:grid-cols-[1fr_1.15fr] items-center">
        <div>
          <h2 id="ruler-title" className="text-[2rem] md:text-[2.6rem] leading-[1.08] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 110' }}>
            How much tread is enough?
          </h2>
          <p className="mt-4 text-white/70 text-[17px] md:text-lg leading-relaxed max-w-md">
            New tires start around 8 mm. Drag the slider to see what each depth means, using the same bands Tread AI puts on
            your report.
          </p>

          <div className="mt-8 hidden md:block" aria-live="polite">
            <Readout mm={mm} n32={thirtySeconds} zone={zone} />
          </div>
        </div>

        <div className="rounded-[1.75rem] bg-white/[0.04] ring-1 ring-white/10 p-5 sm:p-7">
          {/* On phones the readout sits right above what you're dragging */}
          <div className="md:hidden mb-6" aria-live="polite">
            <Readout mm={mm} n32={thirtySeconds} zone={zone} compact />
          </div>

          <svg viewBox={`0 0 ${W} ${BASE + 22}`} className="w-full h-auto" aria-hidden="true">
            <rect x="0" y={BASE} width={W} height="22" rx="8" fill="#2C2D32" />
            {Array.from({ length: blocks }, (_, i) => {
              const x = i * (bw + groove);
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={BASE - h(mm)}
                    width={bw}
                    height={h(mm) + 2}
                    rx="7"
                    fill={atLimit ? '#5B3A39' : '#3D3E45'}
                    style={{ transition: 'y .15s ease, height .15s ease, fill .2s ease' }}
                  />
                  {i < blocks - 1 && (
                    <rect x={x + bw + 3} y={BASE - h(1.6)} width={groove - 6} height={h(1.6)} rx="2" fill="#5A5C66" />
                  )}
                </g>
              );
            })}
          </svg>
          <p className="mt-3 text-xs text-white/45 leading-relaxed">
            The small bars in the grooves are wear bars, molded in at 2/32″. When the tread is flush with them, the tire is at the legal limit.
          </p>

          <label htmlFor="depth" className="sr-only">Tread depth in millimetres</label>
          <input
            id="depth"
            type="range"
            min={MIN}
            max={MAX}
            step={0.1}
            value={mm}
            onChange={(e) => setMm(parseFloat(e.target.value))}
            onPointerDown={jump}
            className="tread-range mt-5"
            style={{ ['--track' as string]: TRACK, ['--thumb' as string]: zone.color }}
            aria-valuetext={`${mm.toFixed(1)} millimetres, ${zone.label}`}
          />
          <div className="relative h-4 text-xs text-white/50 tabular" aria-hidden="true">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <span key={n} className="absolute -translate-x-1/2" style={{ left: `calc(15px + (100% - 30px) * ${(n - MIN) / (MAX - MIN)})` }}>
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Readout = ({ mm, n32, zone, compact }: { mm: number; n32: number; zone: Zone; compact?: boolean }) => (
  <div>
    <div className="flex items-baseline gap-3">
      <span className={`${compact ? 'text-5xl' : 'text-6xl md:text-7xl'} font-semibold tabular tracking-tight`}>
        {mm.toFixed(1)}
        <span className={`${compact ? 'text-xl' : 'text-2xl md:text-3xl'} text-white/60 font-normal ml-1`}>mm</span>
      </span>
      <span className="text-white/60 tabular">{n32}/32″</span>
      {compact && <span className={`ml-auto shrink-0 px-3 py-1 rounded-full text-sm font-medium ${zone.badge}`}>{zone.label}</span>}
    </div>
    <div className={`mt-3 flex items-start gap-3 ${compact ? 'min-h-[3rem]' : 'min-h-[4.5rem]'}`}>
      {!compact && <span className={`shrink-0 mt-0.5 px-3 py-1 rounded-full text-sm font-medium ${zone.badge}`}>{zone.label}</span>}
      <p className="text-white/85 leading-relaxed">{zone.advice}</p>
    </div>
  </div>
);

const STEPS = [
  {
    title: 'Tell it which car',
    body: 'Type your license plate or scan it with the camera. Make, model and tire size are optional, but they sharpen the reading.',
    img: '/app/2-vehicle.jpg',
    alt: 'Vehicle identification screen with license plate, make, model and tire details',
  },
  {
    title: 'Photograph each tire',
    body: 'Two shots per tire: the sidewall, where Tread AI reads the brand, size and DOT date code, and the tread, where it measures depth. A frame on screen shows where to aim.',
    img: '/app/3-four-tires.jpg',
    alt: 'Four-tire screen showing photos and a depth reading for each tire',
  },
  {
    title: 'Read the report',
    body: 'Depth, age and wear pattern for every tire, with a plain-language recommendation. Save it as a PDF or send it to your shop.',
    img: '/app/4-report.jpg',
    alt: 'Inspection report with per-tire depth, age, DOT code and wear pattern',
  },
];

const FAQ = [
  {
    q: 'How accurate is it?',
    a: 'On our test set of real tires, readings are off by about 1.4 mm on average, and closer in the 4.5–6 mm range where most replacement decisions happen. That is enough to tell a healthy tire from a worn one, but not a substitute for a tread gauge when you are near the limit.',
  },
  {
    q: 'What does it cost?',
    a: 'Your first inspection is free. Unlimited inspections come with Tread AI Premium: $3 a month or $33 a year in the US (prices vary by country and are shown in the App Store before you subscribe). Cancel any time in your Apple ID settings.',
  },
  {
    q: 'How does it know how old my tires are?',
    a: 'From the DOT code on the sidewall. The last four digits are the week and year the tire was made, so 3823 means week 38 of 2023. Rubber hardens with age, so tires older than about six years deserve a closer look even with good tread.',
  },
  {
    q: 'Do I need an iPhone?',
    a: 'The full guided inspection is in the iPhone app. You can also upload photos and get a reading in your browser with Check tires online.',
  },
  {
    q: 'What happens to my photos?',
    a: 'They are used to produce your report and stored with your inspection. Details are in the privacy policy.',
  },
];

/* ------------------------------------------------------------------------ */
/* Page                                                                     */
/* ------------------------------------------------------------------------ */

export const Dashboard = () => {
  usePageMeta(null, "Check your tire tread depth, tire age and wear with your iPhone. Tread AI reads all four tires from photos and gives you a shareable report.");
  return (
  <div>
    {/* Hero */}
    <section className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-[-10%] h-[620px] w-[620px] rounded-full bg-[radial-gradient(closest-side,rgba(43,99,230,0.16),rgba(43,99,230,0))]" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 md:pt-20 pb-16 md:pb-24 grid gap-12 md:grid-cols-[1.05fr_1fr] items-center">
        <div className="animate-fade-in">
          <a href="#reviews" className="inline-flex items-center gap-2 rounded-full border border-line/80 bg-white px-3 py-1.5 text-sm text-muted hover:text-ink hover:border-line transition-colors">
            <Stars n={4.3} /> <span><b className="text-ink">4.3</b> on the App Store</span>
          </a>
          <h1
            className="mt-6 text-[2.6rem] sm:text-5xl md:text-[3.9rem] leading-[1.02] font-semibold tracking-[-0.025em] text-ink"
            style={{ fontVariationSettings: '"wdth" 112' }}
          >
            Know if your tires are safe, from eight photos.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted leading-relaxed max-w-xl">
            Tread AI measures tread depth, reads the tire's age off the sidewall, and tells you which tires need attention,
            in about five minutes with your iPhone.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <AppStoreButton placement="home_hero" className="shadow-[0_10px_30px_-10px_rgba(0,0,0,0.45)] hover:-translate-y-0.5 transition-transform" />
            <Link
              to="/inspect"
              className="h-14 inline-flex items-center px-6 rounded-full border border-line text-ink font-medium hover:bg-surface transition-colors"
            >
              Check tires online
            </Link>
          </div>
          <p className="mt-5 text-sm text-muted">First inspection free · iPhone with iOS 13.4 or later</p>
        </div>

        <div className="relative h-[420px] sm:h-[580px] md:h-[620px]" aria-label="Tread AI app screens">
          <div className="absolute left-[4%] top-[10%] w-[44%] max-w-[240px] opacity-95 rotate-[-4deg]">
            <PhoneFrame src="/app/3-four-tires.jpg" alt="Tread AI tire screen with a depth reading on each tire" />
          </div>
          <div className="absolute right-[4%] top-0 w-[52%] max-w-[285px]">
            <PhoneFrame src="/app/4-report.jpg" alt="Inspection report showing 6.9 mm and 8.1 mm readings rated Good" />
          </div>
          {/* Floating result cards */}
          <div className="float-card absolute left-0 sm:left-[2%] bottom-[16%] rounded-2xl bg-white/90 backdrop-blur shadow-[0_18px_40px_-16px_rgba(20,30,60,0.35)] ring-1 ring-black/5 px-4 py-3" style={{ animationDelay: '0.35s, 1.4s' }}>
            <div className="text-xs text-muted">Front left</div>
            <div className="mt-0.5 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular">6.9 mm</span>
              <span className="rounded-full bg-good-bg text-good-fg px-2 py-0.5 text-xs font-semibold">Good</span>
            </div>
          </div>
          <div className="float-card absolute right-0 bottom-[6%] rounded-2xl bg-rubber text-white shadow-[0_18px_40px_-16px_rgba(0,0,0,0.6)] px-4 py-3" style={{ animationDelay: '0.6s, 2.2s' }}>
            <div className="text-xs text-white/60">Tire age · DOT 3823</div>
            <div className="mt-0.5 text-2xl font-semibold tabular">3.1 years</div>
          </div>
        </div>
      </div>
    </section>

    {/* Proof strip */}
    <section className="border-y border-line/60 bg-white">
      <dl className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-10 grid grid-cols-2 gap-6 max-w-3xl">
        {[
          { v: '~5 min', k: 'for all four tires' },
          { v: 'Free', k: 'first inspection' },
        ].map((x) => (
          <div key={x.k}>
            <dd className="text-2xl sm:text-3xl md:text-[2.5rem] leading-none font-semibold tracking-tight tabular" style={{ fontVariationSettings: '"wdth" 115' }}>{x.v}</dd>
            <dt className="mt-2 text-sm md:text-base text-muted">{x.k}</dt>
          </div>
        ))}
      </dl>
    </section>

    <TreadRuler />

    {/* What you get */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 md:pt-28" aria-labelledby="features-title">
      <Reveal>
        <h2 id="features-title" className="text-3xl md:text-[2.6rem] leading-[1.1] font-semibold tracking-tight max-w-2xl" style={{ fontVariationSettings: '"wdth" 110' }}>
          Everything a tire check should tell you
        </h2>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 80}>
            <div className="group h-full rounded-3xl bg-rubber text-white p-5 transition-transform duration-300 hover:-translate-y-1">
              <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-5">{f.art}</div>
              <h3 className="mt-5 text-lg font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-white/65 leading-relaxed text-[15px]">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Steps */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 md:py-28" aria-labelledby="steps-title">
      <h2 id="steps-title" className="text-3xl md:text-[2.6rem] leading-[1.1] font-semibold tracking-tight max-w-2xl" style={{ fontVariationSettings: '"wdth" 110' }}>
        One inspection, start to finish
      </h2>
      <ol className="mt-14 space-y-20 md:space-y-28">
        {STEPS.map((s, i) => (
          <li key={s.title}><Reveal className={`grid gap-10 md:grid-cols-2 items-center ${i % 2 ? 'md:[&>div:first-child]:order-2' : ''}`}>
            <div className="max-w-md">
              <span className="inline-flex w-9 h-9 rounded-full bg-brand-600 text-white items-center justify-center font-semibold tabular" aria-hidden="true">
                {i + 1}
              </span>
              <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-3 text-lg text-muted leading-relaxed">{s.body}</p>
            </div>
            <div className="flex justify-center">
              <PhoneFrame src={s.img} alt={s.alt} className="w-[62%] max-w-[290px]" />
            </div>
          </Reveal></li>
        ))}
      </ol>
    </section>

    {/* Reviews */}
    <section id="reviews" className="scroll-mt-20 max-w-6xl mx-auto px-4 sm:px-6 pb-20 md:pb-28">
      <Reveal>
        <ReviewSlider />
      </Reveal>
    </section>

    {/* Tire shops */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 md:pb-28" aria-label="Tire shops">
      <Reveal>
        <TireShops placement="home" />
      </Reveal>
    </section>

    {/* Browser option */}
    <section className="bg-brand-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="max-w-xl">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-brand-900">No iPhone handy?</h2>
          <p className="mt-2 text-brand-900/80 text-lg leading-relaxed">
            Upload tread photos from any computer or phone and get the same depth reading in your browser.
          </p>
        </div>
        <Link
          to="/inspect"
          className="h-14 inline-flex items-center justify-center px-7 rounded-full bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shrink-0"
        >
          Check tires online
        </Link>
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20 md:py-28" aria-labelledby="faq-title">
      <h2 id="faq-title" className="text-3xl md:text-[2.6rem] leading-[1.1] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 110' }}>
        Questions
      </h2>
      <div className="mt-10 divide-y divide-line/70 border-y border-line/70">
        {FAQ.map((f) => (
          <details key={f.q} className="group py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium text-ink">
              {f.q}
              <svg className="w-5 h-5 shrink-0 text-muted transition-transform group-open:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </summary>
            <p className="mt-3 text-muted leading-relaxed max-w-[65ch]">{f.a}</p>
          </details>
        ))}
      </div>
      <p className="mt-8 text-muted">
        Something else? Email{' '}
        <a href="mailto:contact@tread-ai.com" className="text-brand-700 underline underline-offset-2">contact@tread-ai.com</a> or{' '}
        <a href="mailto:sizarta@gmail.com" className="text-brand-700 underline underline-offset-2">sizarta@gmail.com</a>,{' '}
        or call <a href="tel:+19495584019" className="text-brand-700 underline underline-offset-2 tabular">(949) 558-4019</a>.
      </p>
    </section>

    {/* Closing CTA */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 md:pb-28">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-rubber text-white p-8 md:p-14 grid gap-10 md:grid-cols-[1.4fr_1fr] items-center">
          <svg aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-1/2 opacity-[0.06]" preserveAspectRatio="none" viewBox="0 0 100 100">
            {Array.from({ length: 8 }, (_, i) => (
              <path key={i} d={`M${8 + i * 12} 0 l6 12 -6 12 6 12 -6 12 6 12 -6 12 6 12 -6 12 6 12`} stroke="#fff" strokeWidth="2.2" fill="none" vectorEffect="non-scaling-stroke" />
            ))}
          </svg>
          <div className="relative">
            <h2 className="text-3xl md:text-[2.8rem] leading-[1.08] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 112' }}>
              Check all four tires before your next long drive.
            </h2>
            <p className="mt-4 text-white/70 text-lg max-w-lg">Your first inspection is free. It takes about five minutes.</p>
            <AppStoreButton placement="home_closing" className="mt-8 ring-1 ring-white/25" />
          </div>
          <div className="relative hidden md:flex justify-end">
            <div className="rounded-3xl bg-white p-5 text-center shadow-2xl">
              <img src="/app-store-qr.svg" alt="QR code linking to Tread AI on the App Store" className="w-40 h-40" />
              <p className="mt-3 text-sm font-medium text-ink">Scan with your iPhone</p>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  </div>
);
};
